import React, { useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';

import {
  InternalApiLibraryScenario,
  InternalApiScenarioEntityType,
  InternalApiScenarioIntervalTimeUnit,
  InternalApiScenarioLanguage,
  InternalApiScenarioTypeV2,
  InternalApiScenarioVersion,
  InternalApiScenarioVersionStatus,
} from '../../../api-internal';
import ScenarioVersionRightPanel from './ScenarioVersionRightPanel';
import ScenarioVersionLeftPanel from './ScenarioVersionLeftPanel';
import './ScenarioVersionModal.scss';
import { QueryValidationStatus, ScenarioVersionModalFormValues } from './ScenarioVersionModal';
import { Formik } from 'formik';
import dayjs from 'dayjs';
import { DATE_TIME_FORMAT } from '../../../common/helper';
import { SalvModal } from '@salvapp/ui-components';

export interface LibraryScenarioVersionModalProps {
  scenario: InternalApiLibraryScenario;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
}

export const LibraryScenarioVersionModal = ({
  scenario,
  showModal,
  setShowModal,
}: LibraryScenarioVersionModalProps) => {
  const [queryValidationStatus, setQueryValidationStatus] = useState<QueryValidationStatus>(
    QueryValidationStatus.NOT_STARTED
  );

  const isPeriodic = scenario.type.includes(InternalApiScenarioTypeV2.PERIODIC);
  const isPeriodicEntity = scenario.type.includes(InternalApiScenarioTypeV2.PERIODIC_ENTITY);
  const isDraft = true;
  const closeModal = () => {
    setShowModal(false);
  };

  const version: InternalApiScenarioVersion = {
    id: '1',
    versionId: 1,
    status: InternalApiScenarioVersionStatus.DRAFT,
    updatedBy: {
      name: 'Salv',
    },
    updatedTime: dayjs().toDate(),
    parameters: scenario.parameters || [],
    logic: scenario.logic,
    query: scenario.query,
    details: scenario.details,
    language: InternalApiScenarioLanguage.SQL,
    segments: scenario.segments,
    includeRelatedTransactions: scenario.includeRelatedTransactions,
  };

  const periodicScenarioAttributes = {
    startTime: dayjs().format(DATE_TIME_FORMAT),
    periodicIntervalTimeUnit: InternalApiScenarioIntervalTimeUnit.WEEK,
    periodicInterval: 1,
  };

  const initialValues: ScenarioVersionModalFormValues = useMemo(
    () => ({
      details: version?.details || '',
      includeRelatedTransactions: version?.includeRelatedTransactions || false,
      query: version.query || '',
      note: version?.note || '',
      status: version?.status || InternalApiScenarioVersionStatus.DRAFT,
      language: version.language,
      suppressionInterval: 0,
      ...(isPeriodic && periodicScenarioAttributes),
      parameters: version?.parameters || [],
      logic: version?.logic || '',
      segments: version.segments || [],
      excludedSegments: version.excludedSegments || [],
    }),
    [version]
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => {
        // ignore
      }}
      validateOnChange={false}
      enableReinitialize={true}
    >
      {() => {
        return (
          <SalvModal show={showModal} onHide={closeModal} className='scenario-version-modal'>
            <Form>
              <SalvModal.Header>View library scenario {scenario.internalId} version 1</SalvModal.Header>
              <SalvModal.Body>
                <div className='scenario-version-modal-container'>
                  <div className='left-panel'>
                    <ScenarioVersionLeftPanel
                      isDraft={isDraft}
                      isDisabled={true}
                      isPeriodic={isPeriodic}
                      isPeriodicEntity={isPeriodicEntity}
                      isTransactionScenario={scenario.entityType === InternalApiScenarioEntityType.TRANSACTION}
                    />
                  </div>

                  <ScenarioVersionRightPanel
                    scenarioId={scenario.id}
                    isDisabled={true}
                    isLibraryScenario={true}
                    isPeriodic={isPeriodic || isPeriodicEntity}
                    queryValidationStatus={queryValidationStatus}
                    setQueryValidationStatus={setQueryValidationStatus}
                    entityType={scenario.entityType}
                  />
                </div>
              </SalvModal.Body>
            </Form>
          </SalvModal>
        );
      }}
    </Formik>
  );
};

export default LibraryScenarioVersionModal;
